import {getAuth} from 'firebase/auth';
import Swal from 'sweetalert2';
import {reloadPage} from 'appReload';
import {isBrowserOffline, OFFLINE_MESSAGE} from 'utils/connectionMessages';
import {
  getSyncMessageServiceWorkerErrorKind,
  type SyncMessageServiceWorkerErrorKind,
} from 'utils/syncMessageServiceWorker';
import {
  reportSyncMessageStartupError,
  type SyncMessageStartupErrorType,
} from 'utils/reportSyncMessageStartupError';
import {countFailedPageLoad} from 'utils/syncMessageFailedPageLoads';

const UNREACHABLE_MESSAGE =
  "We couldn't reach the course website. Check your internet connection, then try again.";
const RESTART_MESSAGE =
  "This page didn't finish starting. Reload the page. If that doesn't work, close this tab and open the page in a new one.";
const BROWSER_RESTART_MESSAGE =
  "This page couldn't start in your browser. Quit your browser completely and open it again, since it may have installed an update that needs a restart. Then come back to this page.";
/** Failed page loads in one tab after which a reload has evidently not helped. */
const REPEATED_FAILURE_THRESHOLD = 2;
const APP_BROWSER_SIGN_IN_MESSAGE =
  "This app's built-in browser can't run this site. Open this page in Safari or Chrome to sign in.";
const APP_BROWSER_RUN_CODE_MESSAGE =
  "This app's built-in browser can't run this site. Open this page in Safari or Chrome to run code.";
const UNSUPPORTED_BROWSER_MESSAGE =
  "This browser can't run code on this site. Open this page in a current version of Chrome, Safari, Firefox or Edge.";
const STILL_OFFLINE_MESSAGE = "You're still offline.";
const LINK_COPIED_MESSAGE = 'Link copied. Paste it into Safari or Chrome.';
const COPY_ADDRESS_PREFIX = 'Copy this address:';

/**
 * What registration rejects with when the worker script could not be
 * downloaded: Chromium's "…when fetching the script.", WebKit's (every iPhone
 * browser) "Script <url> load failed" and Firefox's "Load failed with status".
 */
const SCRIPT_DOWNLOAD_FAILURE_PATTERN =
  /when fetching the script|load failed|NetworkError|Failed to fetch/i;

/** iPhone, iPad and iPod user agents. An iPad asking for desktop sites reports a Mac and is not matched. */
const IOS_DEVICE_PATTERN = /iPhone|iPad|iPod/;

/** Tokens apps add to the user agent of their built-in browser. */
const APP_BROWSER_TOKEN_PATTERN =
  /GSA\/|LinkedInApp|FBAN|FBAV|Instagram|Twitter|Line\/|MicroMessenger|Snapchat|musical_ly|BytedanceWebview|Pinterest/;

/**
 * How long to wait for Firebase to restore the session before choosing the
 * signed-out wording; a stuck IndexedDB on iOS would otherwise hold the prompt.
 */
const AUTH_READY_WAIT_MS = 2_000;

/**
 * Colors darker than SweetAlert's defaults, which put white button text at
 * only 4.5:1 and the warning icon at 1.7:1 on the popup's white. These keep
 * the buttons above 6:1 and the icon above 3:1.
 */
const CONFIRM_BUTTON_COLOR = '#5a50c8';
const CANCEL_BUTTON_COLOR = '#5c636a';
const ICON_COLOR = '#b45309';

/** What the prompt's main button does. */
export type ServiceWorkerFailureAction =
  | 'retry'
  | 'reload'
  | 'copy-link'
  | 'close';

const BUTTON_TEXT: Record<ServiceWorkerFailureAction, string> = {
  retry: 'Try again',
  reload: 'Reload',
  'copy-link': 'Copy link',
  close: 'Close',
};
const STORAGE_UNAVAILABLE_MESSAGE =
  "Your browser couldn't save data for this site, so code can't run. In Chrome, open Site settings for this site, allow cookies and site data, then clear this site's data and reload. Also check that your computer has free disk space. If it still fails, try a different Chrome profile or another browser.";

interface ServiceWorkerFailureParams {
  error?: unknown;
  serviceWorkerSupported: boolean;
}

/** The reader's situation. It picks the wording, never the diagnostic type. */
interface ServiceWorkerFailureContext {
  /** Whether the browser reports being offline. Omitted → false. */
  isOffline?: boolean;
  /** The browser's user agent. Omitted → '' (no in-app browser detected). */
  userAgent?: string;
  /** Whether someone is signed in. Omitted → false. */
  isSignedIn?: boolean;
  /**
   * Failed page loads in this tab, including this one. Omitted → 1 (the
   * first).
   */
  sessionFailureCount?: number;
}

/** A classified failure: what to record, and what to tell the user. */
interface ClassifiedServiceWorkerFailure {
  /** The diagnostic type stored with the report. */
  errorType: SyncMessageStartupErrorType;
  /** The message shown to the reader. */
  message: string;
  /** What the prompt's main button does. */
  action: ServiceWorkerFailureAction;
}

function isIosAppBrowser(userAgent: string): boolean {
  if (!IOS_DEVICE_PATTERN.test(userAgent)) {
    return false;
  }
  // Safari and the other iOS browsers keep the Safari token; an app's own
  // web view drops it unless the app adds it back.
  return (
    APP_BROWSER_TOKEN_PATTERN.test(userAgent) || !userAgent.includes('Safari/')
  );
}

function classifyUnsupported({
  userAgent,
  isSignedIn,
}: Required<
  Pick<ServiceWorkerFailureContext, 'userAgent' | 'isSignedIn'>
>): ClassifiedServiceWorkerFailure {
  if (isIosAppBrowser(userAgent)) {
    return {
      errorType: 'service-worker-disabled',
      message: isSignedIn
        ? APP_BROWSER_RUN_CODE_MESSAGE
        : APP_BROWSER_SIGN_IN_MESSAGE,
      action: 'copy-link',
    };
  }
  return {
    errorType: 'service-worker-disabled',
    message: UNSUPPORTED_BROWSER_MESSAGE,
    action: 'close',
  };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? '');
}

/**
 * Maps a sync-message service-worker startup failure onto its diagnostic type
 * and the matching user-facing message and action. Exported for tests;
 * callers should prefer {@link handleServiceWorkerError} or
 * {@link reportServiceWorkerFailure}.
 *
 * @param params.error The underlying error, if any; used to classify the
 *   failure. Omitted → treated as a generic registration failure.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers; `false` short-circuits to the "unsupported" message.
 * @param params.isOffline Whether the browser reports being offline. Omitted
 *   → false.
 * @param params.userAgent The browser's user agent, to tell an iPhone app's
 *   built-in browser apart. Omitted → '' (not an app browser).
 * @param params.isSignedIn Whether someone is signed in, for the app-browser
 *   wording. Omitted → false (the sign-in wording).
 * @param params.sessionFailureCount Failed page loads in this tab, including
 *   this one. Omitted → 1. At `REPEATED_FAILURE_THRESHOLD` and above, reload
 *   advice gives way to a browser restart, as it does for a worker script that
 *   failed to evaluate.
 * @returns The diagnostic type, the message and the button's action.
 */
export function classifyServiceWorkerFailure({
  error,
  serviceWorkerSupported,
  isOffline = false,
  userAgent = '',
  isSignedIn = false,
  sessionFailureCount = 1,
}: ServiceWorkerFailureParams &
  ServiceWorkerFailureContext): ClassifiedServiceWorkerFailure {
  if (!serviceWorkerSupported) {
    return classifyUnsupported({userAgent, isSignedIn});
  }

  const errorKind = getSyncMessageServiceWorkerErrorKind(error);
  if (errorKind === 'unsupported') {
    return classifyUnsupported({userAgent, isSignedIn});
  }
  if (errorKind === 'storage_unavailable') {
    return {
      errorType: 'service-worker-registration-failed',
      message: STORAGE_UNAVAILABLE_MESSAGE,
      action: 'reload',
    };
  }

  const errorType: SyncMessageStartupErrorType =
    errorKind === 'controller_unavailable'
      ? 'service-worker-tab-recovery-needed'
      : 'service-worker-registration-failed';
  if (isOffline) {
    return {errorType, message: OFFLINE_MESSAGE, action: 'retry'};
  }
  // Firefox fails to evaluate the worker while a browser update is pending;
  // reloading cannot fix that, and neither can a second reload of anything.
  if (
    errorKind === 'script_evaluation_failed' ||
    sessionFailureCount >= REPEATED_FAILURE_THRESHOLD
  ) {
    return {errorType, message: BROWSER_RESTART_MESSAGE, action: 'close'};
  }
  if (
    errorKind === 'unknown' &&
    SCRIPT_DOWNLOAD_FAILURE_PATTERN.test(messageOf(error))
  ) {
    return {errorType, message: UNREACHABLE_MESSAGE, action: 'retry'};
  }
  return {errorType, message: RESTART_MESSAGE, action: 'reload'};
}

async function hasSignedInUser(): Promise<boolean> {
  try {
    const auth = getAuth();
    await Promise.race([
      auth.authStateReady(),
      new Promise(resolve => setTimeout(resolve, AUTH_READY_WAIT_MS)),
    ]);
    return auth.currentUser !== null;
  } catch (e) {
    console.debug(
      '[handleServiceWorkerError] could not read the auth state',
      e,
    );
    return false;
  }
}

// SweetAlert labels its popup by a title, which this prompt doesn't have, and
// calls it a plain dialog; name it by its message and mark it as an alert.
function labelAsAlertDialog(popup: HTMLElement): void {
  popup.setAttribute('role', 'alertdialog');
  const messageId = Swal.getHtmlContainer()?.id;
  if (!messageId) {
    return;
  }
  popup.setAttribute('aria-labelledby', messageId);
  popup.removeAttribute('aria-describedby');
}

async function copyPageLink(message: string): Promise<void> {
  const link = window.location.href;
  try {
    await navigator.clipboard.writeText(link);
    Swal.update({text: `${message} ${LINK_COPIED_MESSAGE}`});
  } catch (e) {
    console.debug('[handleServiceWorkerError] could not copy the page link', e);
    Swal.update({text: `${message} ${COPY_ADDRESS_PREFIX} ${link}`});
  }
}

/** Runs the main button's action; false keeps the prompt open. */
async function runAction({
  action,
  message,
}: Pick<
  ClassifiedServiceWorkerFailure,
  'action' | 'message'
>): Promise<boolean> {
  switch (action) {
    case 'retry':
      if (isBrowserOffline()) {
        Swal.showValidationMessage(STILL_OFFLINE_MESSAGE);
        return false;
      }
      reloadPage({reason: 'service-worker-missing'});
      return true;
    case 'reload':
      reloadPage({reason: 'service-worker-missing'});
      return true;
    case 'copy-link':
      await copyPageLink(message);
      return false;
    case 'close':
      return true;
  }
}

function showServiceWorkerFailure({
  message,
  action,
}: ClassifiedServiceWorkerFailure): void {
  void Swal.fire({
    icon: 'warning',
    iconColor: ICON_COLOR,
    text: message,
    confirmButtonText: BUTTON_TEXT[action],
    confirmButtonColor: CONFIRM_BUTTON_COLOR,
    showCancelButton: action !== 'close',
    cancelButtonText: BUTTON_TEXT.close,
    cancelButtonColor: CANCEL_BUTTON_COLOR,
    didOpen: labelAsAlertDialog,
    preConfirm: () => runAction({action, message}),
  });
}

/**
 * Records a sync-message service-worker startup failure without interrupting
 * the user.
 *
 * For the boot-time warm-up in `serviceWorkerController`, which runs from
 * `bootstrapCipApp` before the first render — so before routing, and therefore
 * also on pages with no code runner at all (the landing page, `/cipx/intro`),
 * where a modal about service workers means nothing to the reader. Pages that
 * can actually run code prompt through {@link handleServiceWorkerError}
 * instead, when `CodeProvider` asks for the runtime.
 *
 * @param params.error The underlying error, if any; captured in the diagnostic.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers.
 * @returns Resolves once the diagnostic write attempt settles.
 * @throws Never — the diagnostic write is best-effort and swallowed on failure.
 */
export async function reportServiceWorkerFailure(
  params: ServiceWorkerFailureParams,
): Promise<void> {
  const sessionFailureCount = countFailedPageLoad();
  const {errorType} = classifyServiceWorkerFailure(params);
  await reportSyncMessageStartupError(errorType, params.error, {
    errorKind: errorKindOf(params),
    sessionFailureCount,
  });
}

/**
 * The classified cause recorded with a report.
 *
 * @param params - The failure being reported.
 * @return The error kind, or `null` when service workers are unsupported.
 * @throws Never.
 */
function errorKindOf({
  error,
  serviceWorkerSupported,
}: ServiceWorkerFailureParams): SyncMessageServiceWorkerErrorKind | null {
  return serviceWorkerSupported
    ? getSyncMessageServiceWorkerErrorKind(error)
    : null;
}

/**
 * Shows the user an appropriate recovery prompt for a fatal sync-message
 * service-worker startup failure and records a diagnostic to Firestore. The
 * message is chosen from the classified error kind and the reader's situation:
 * offline, a failed script download, an iPhone app's built-in browser, or a
 * browser that can't run the worker at all.
 *
 * Only for a page that actually wants the code runner, since the prompt is a
 * modal — `CodeProvider.requestPythonClient` is the caller, because that is
 * the first point where we know the runtime was wanted. Non-fatal telemetry
 * (e.g. update timeouts) reports directly via
 * {@link reportSyncMessageStartupError} without an alert.
 *
 * @param params.error The underlying error, if any; used to classify the
 *   message and captured in the diagnostic. Omitted → treated as a generic
 *   registration failure.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers; `false` short-circuits to the "unsupported" prompt.
 * @returns Resolves once the diagnostic write attempt settles.
 * @throws Never — the diagnostic write is best-effort and swallowed on failure.
 */
export async function handleServiceWorkerError(
  params: ServiceWorkerFailureParams,
): Promise<void> {
  const sessionFailureCount = countFailedPageLoad();
  const failure = classifyServiceWorkerFailure({
    ...params,
    isOffline: isBrowserOffline(),
    userAgent: navigator.userAgent,
    isSignedIn: await hasSignedInUser(),
    sessionFailureCount,
  });
  showServiceWorkerFailure(failure);
  await reportSyncMessageStartupError(failure.errorType, params.error, {
    errorKind: errorKindOf(params),
    sessionFailureCount,
  });
}
